/**
 * A dialog shell for the media library.
 *
 * Deliberately NOT a replacement for `components/common/Modal` — everything
 * else in the app should keep using that one. The library needs two things the
 * shared modal cannot currently give without editing it (and editing it would
 * restyle every modal in the product):
 *
 *   * dark-mode surfaces — the shared panel is hardcoded `bg-white`, so an
 *     image grid or an editor canvas sits on a white card in dark mode;
 *   * widths past `max-w-4xl` — the editor needs a canvas, a control column
 *     and a live preview side by side.
 *
 * The transitions, overlay and close affordance are copied from the shared
 * modal on purpose, so the two feel identical.
 */

import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

import { cn } from '@/utils';

export type MediaDialogSize = 'md' | 'lg' | 'xl' | '2xl' | '4xl' | '6xl' | '7xl';

const SIZES: Record<MediaDialogSize, string> = {
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '4xl': 'max-w-4xl',
  '6xl': 'max-w-6xl',
  '7xl': 'max-w-7xl',
};

interface MediaDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  size?: MediaDialogSize;
  /** Extra header content (filters, tabs) rendered under the title. */
  toolbar?: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
  /** Set while an upload is in flight so a stray click cannot abandon it. */
  closeOnOverlayClick?: boolean;
  bodyClassName?: string;
}

export function MediaDialog({
  isOpen,
  onClose,
  title,
  description,
  size = '4xl',
  toolbar,
  footer,
  children,
  closeOnOverlayClick = true,
  bodyClassName,
}: MediaDialogProps) {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={closeOnOverlayClick ? onClose : () => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={cn(
                  'w-full transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all dark:bg-gray-800',
                  SIZES[size],
                )}
              >
                {(title || toolbar) && (
                  <div className="border-b border-gray-100 px-6 py-4 dark:border-gray-700">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        {title && (
                          <Dialog.Title
                            as="h3"
                            className="truncate text-lg font-semibold text-gray-900 dark:text-white"
                          >
                            {title}
                          </Dialog.Title>
                        )}
                        {description && (
                          <Dialog.Description className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            {description}
                          </Dialog.Description>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                    {toolbar && <div className="mt-4">{toolbar}</div>}
                  </div>
                )}

                <div className={cn('px-6 py-5', bodyClassName)}>{children}</div>

                {footer && (
                  <div className="flex flex-wrap items-center justify-end gap-3 border-t border-gray-100 bg-gray-50 px-6 py-4 dark:border-gray-700 dark:bg-gray-800/60">
                    {footer}
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
